import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import _isEmpty from 'lodash/isEmpty';
import _pick from 'lodash/pick';
import _without from 'lodash/without';
import { TaskScopeNameEnum, TaskTimelineFrameEnum } from 'graphql-common';
import Table from '@lib/components/Table/Table';
import CircleLoader from '@lib/components/CircleLoader/CircleLoader';
import Search from '@lib/components/Search/Search';
import PageBody from '@lib/layouts/DashboardLayout/PageBody/PageBody';
import EmptyState from '@lib/components/EmptyState/EmptyState';
import { IconName } from '@lib/components/Modal/enums';
import useSearchParam from '@lib/hooks/useSearchParam';
import Trans from '@lib/components/Trans/Trans';
import ErrorPage from '@lib/components/ErrorPage/ErrorPage';
import getStatusFromError from '@lib/utils/getStatusFromError';
import getDescriptionFromError from '@lib/utils/getDescriptionFromError';
import Modal from '@lib/components/Modal/Modal';
import GenerateReportForm from 'components/GenerateReportForm/GenerateReportForm';
import useModalManagement, { Modals } from '@lib/hooks/useModalManagement';
import BreadCrumbs from '@lib/components/BreadCrumbs/BreadCrumbs';
import { getBreadCrumbsDataByKey } from 'utils/breadcrumbs';
import PageHeader from '@lib/layouts/DashboardLayout/PageHeader/PageHeader';
import { APP_URLS } from 'constants/urls';
import Button, { ButtonTypes } from '@lib/components/Button/Button';
import TasksFilterFieldNames from '@lib/enums/fieldNames/tasksFilterFieldNames';
import { useLocation } from 'react-router-dom';
import TasksTimelineStateCounters from './TasksTimelineStateCounters';
import useActions from './utils/useActions';
import getEmptyStateTextKeys from './utils/getEmptyStateTextKeys';
import { getAdditionalColumns } from './utils/getAdditionalColumns';
import mergeTasksWithTimeline from './utils/mergeTasksWithTimeline';
import getTimelineTableColumns from './utils/getTimelineTableColumns';
import TableTopHeadComponent from './TableTopHeadComponent';
import TasksTimelineFilter from './TasksTimelineFilter';

function TasksTimeline() {
  const { pathname } = useLocation();
  const useModalManagementResult = useModalManagement([
    Modals.GenerateReportModal,
  ]);
  const {
    endDateParam,
    filterParams,
    isPeriodCustomized,
    onEndDateChange,
    onUpdateFilterParams,
    onViewChange,
    periodEndParam,
    periodStartParam,
    maxDate,
    minDate,
    resetAllFilterParams,
    resetFilterParams,
    resetPeriodFilterParams,
    taskCounterData,
    tasks,
    tasksError,
    tasksFirstLoading,
    tasksLoading,
    timelineData = [],
    timelineGroupBy = TaskTimelineFrameEnum.Year,
    timelineLoading,
    totalTasksCount,
    view,
    onReportCreateAction,
    taskTimelineReportCreateMutationResult,
    basePeriodStartParam,
    basePeriodEndParam,
  } = useActions({ useModalManagementResult });
  const { t } = useTranslation();
  const [isFullTableView, setFullTableView] = useState(false);
  const [searchQuery, setSearchQuery] = useSearchParam();

  const isFilterApplied =
    Object.values(
      _pick(
        filterParams,
        TasksFilterFieldNames.Site,
        TasksFilterFieldNames.FormCategory,
        TasksFilterFieldNames.ValidityHints,
      ),
    ).filter((value) => {
      if (Array.isArray(value)) {
        return !!_without(value, '').length;
      }
      return !_isEmpty(value);
    }).length > 0;

  const hasActiveFilter =
    !!searchQuery || isFilterApplied || isPeriodCustomized;

  const resetAllFilters = () => {
    if (searchQuery) setSearchQuery('');
    resetAllFilterParams();
  };

  const openGenerateReportModal = () =>
    useModalManagementResult.openModal(Modals.GenerateReportModal);

  // Actions
  const isGenerateReportModalOpened = useModalManagementResult.isModalOpened(
    Modals.GenerateReportModal,
  );
  const closeGenerateReportModal = () =>
    useModalManagementResult.closeModal(Modals.GenerateReportModal);

  const onToggleFullTableView = () => {
    setFullTableView((prevState) => !prevState);
  };

  const tableColumns = getTimelineTableColumns({
    columns: getAdditionalColumns({
      t,
      view: timelineGroupBy,
      endDateParam,
    }),
    isFullTableView,
    t,
  });

  const [emptyStateTitleKey, emptyStateTextKey] = getEmptyStateTextKeys(
    TaskScopeNameEnum.DashboardAll,
  );

  const emptyState = hasActiveFilter ? (
    <EmptyState
      title={t('no-results-found')}
      text={t('no-results-found-text')}
      icon={IconName.SearchOff}
      actions={[
        <Button
          key="back"
          buttonText={t('go-back')}
          buttonType={ButtonTypes.primaryFilled}
          leftIcon="arrow_back"
          onClick={resetAllFilters}
        />,
      ]}
    />
  ) : (
    <EmptyState
      title={t(emptyStateTitleKey)}
      text={<Trans i18nKey={emptyStateTextKey} />}
      icon={IconName.Cancel}
    />
  );

  const topHeadComponent = () => (
    <TableTopHeadComponent
      endDate={endDateParam}
      isFullTableView={isFullTableView}
      onEndDateChange={onEndDateChange}
      onToggleFullTableView={onToggleFullTableView}
      onViewChange={onViewChange}
      periodEndParam={periodEndParam}
      periodStartParam={periodStartParam}
      view={view}
    />
  );

  return tasksError ? (
    <ErrorPage
      status={getStatusFromError(tasksError)}
      description={getDescriptionFromError(tasksError)}
    />
  ) : (
    <>
      <BreadCrumbs data={getBreadCrumbsDataByKey({ pathname, t })} />
      <PageHeader title={t('timeline')}>
        {pathname === APP_URLS.dashboard.timeline.path ? (
          <Button
            buttonText={t('generate-report')}
            onClick={openGenerateReportModal}
          />
        ) : undefined}
      </PageHeader>
      <TasksTimelineFilter
        filterParams={filterParams}
        resetPeriodFilterParams={resetPeriodFilterParams}
        resetFilterParams={resetFilterParams}
        onUpdateFilterParams={onUpdateFilterParams}
        minDate={minDate}
        maxDate={maxDate}
        basePeriodStartParam={basePeriodStartParam}
        basePeriodEndParam={basePeriodEndParam}
      />
      <TasksTimelineStateCounters
        taskCounterData={taskCounterData}
        tasksError={tasksError}
        tasksFirstLoading={tasksFirstLoading}
        filterParams={filterParams}
      />
      <PageBody>
        {tasksFirstLoading ? (
          <CircleLoader />
        ) : (
          <>
            {(!!tasks?.length || hasActiveFilter) && (
              <Search placeholder={t('tasks-timeline-placeholder')} />
            )}
            <Table
              columns={tableColumns}
              data={mergeTasksWithTimeline(tasks, timelineData)}
              emptyState={emptyState}
              fullHeight={false}
              id="tasks"
              loading={tasksLoading || timelineLoading}
              totalEntries={totalTasksCount}
              components={{
                TopHeadComponent: topHeadComponent,
              }}
            />
          </>
        )}
      </PageBody>
      <Modal
        isOpen={isGenerateReportModalOpened}
        onClose={closeGenerateReportModal}
        title={t('generate-report')}
      >
        <GenerateReportForm
          loading={taskTimelineReportCreateMutationResult.loading}
          onCancel={closeGenerateReportModal}
          graphQLErrors={
            taskTimelineReportCreateMutationResult.error?.graphQLErrors
          }
          onSubmit={onReportCreateAction}
          filterParams={filterParams}
          basePeriodStartParam={basePeriodStartParam}
          basePeriodEndParam={basePeriodEndParam}
        />
      </Modal>
    </>
  );
}

export default TasksTimeline;
